import { useState } from 'react';
import { FieldArray, useFormikContext, getIn } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormGroup,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Plus, Trash2 as Trash, Undo2 as Undo } from 'lucide-react';
import { v4 } from 'uuid';
import InputText from '@/components/InputText';
import { Content, Setting, SettingType } from '@/types';

const EMPTY_SETTING = {
  title: '',
  price: null,
  type: SettingType.OPTIONS,
};

const EMPTY_OPTION = {
  title: '',
  price: 0,
  type: 'option',
};

const EMPTY_SUBTITLE = {
  title: '',
  type: 'subtitle',
};

export const SettingsSchema = Yup.object().shape({
  settings: Yup.array().of(
    Yup.object().shape({
      title: Yup.string(),
      options: Yup.array().of(
        Yup.object().shape({
          title: Yup.string()
            .min(4, 'Muy Corto!')
            .max(50, 'Muy Largo!')
            .required('Campo requerido'),
          price: Yup.number(),
        })
      ),
    })
  ),
});

const Settings = () => {
  const { values, errors, touched, setFieldValue } =
    useFormikContext<Content>();
  const [type] = useState<SettingType>(SettingType.OPTIONS ?? '');

  if (!values.settings || !Array.isArray(values.settings)) {
    return null;
  }

  return (
    <Stack spacing={2}>
      <FieldArray name="settings">
        {({ push, replace }) => {
          const addOption = (
            si: number,
            type: 'option' | 'subtitle' = 'option'
          ) => {
            const settings = (values?.settings ?? []) as Setting[];

            if (!settings?.[si]) return;

            if (settings[si]?.options) {
              replace(si, {
                ...settings[si],
                options: [
                  ...settings[si].options,
                  {
                    ...(type === 'option' ? EMPTY_OPTION : EMPTY_SUBTITLE),
                    id: v4(),
                  },
                ],
              });
            } else {
              replace(si, {
                ...settings[si],
                options: [
                  {
                    ...(type === 'option' ? EMPTY_OPTION : EMPTY_SUBTITLE),
                    id: v4(),
                  },
                ],
              });
            }
          };

          return (
            <Stack spacing={2}>
              {((values?.settings as Setting[]) ?? []).map((s, si) => (
                <Paper
                  key={`${s}-${si}`}
                  elevation={2}
                  sx={{
                    padding: 2,
                    borderRadius: 2,
                  }}
                >
                  {s?.softDelete ? (
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography
                        variant="h4"
                        sx={{ textDecoration: 'line-through' }}
                      >
                        {s?.title ?? ''}
                      </Typography>
                      <Tooltip
                        arrow
                        placement="top"
                        title="Restaurar Configuración"
                      >
                        <IconButton
                          onClick={() => {
                            replace(si, {
                              ...s,
                              softDelete: false,
                            });
                          }}
                        >
                          <Undo />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  ) : (
                    <Stack spacing={2}>
                      <Stack
                        direction={'row'}
                        spacing={2}
                        alignItems={'center'}
                      >
                        <InputText
                          label="Título"
                          name={`settings[${si}].title`}
                          size="small"
                          value={
                            (values.settings as Setting[])[si]?.title ?? ''
                          }
                          error={Boolean(
                            getIn(touched, `settings.${si}.title`) &&
                              getIn(errors, `settings.${si}.title`)
                          )}
                          helperText={
                            getIn(touched, `settings.${si}.title`) &&
                            getIn(errors, `settings.${si}.title`)
                          }
                        />
                        <Tooltip
                          title="Eliminar Configuración"
                          arrow
                          placement="top"
                        >
                          <IconButton
                            color="error"
                            onClick={() => {
                              replace(si, {
                                ...s,
                                softDelete: true,
                              });
                            }}
                          >
                            <Trash />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                      <Stack direction={'row'} spacing={2}>
                        {/* MIN-MAX */}
                        <Stack direction={'row'} spacing={2} flex={1}>
                          <InputText
                            label="Mínimo"
                            type="number"
                            name={`settings[${si}].min`}
                            size="small"
                            value={
                              Number(
                                (values?.settings as Setting[])[si]?.min
                              ) ?? 1
                            }
                            error={Boolean(
                              getIn(touched, `settings.${si}.min`) &&
                                getIn(errors, `settings.${si}.min`)
                            )}
                            helperText={
                              getIn(touched, `settings.${si}.min`) &&
                              getIn(errors, `settings.${si}.min`)
                            }
                          />
                          <InputText
                            label="Máximo"
                            type="number"
                            name={`settings[${si}].max`}
                            size="small"
                            value={
                              Number((values?.settings as Setting[])[si].max) ??
                              1
                            }
                            error={Boolean(
                              getIn(touched, `settings.${si}.max`) &&
                                getIn(errors, `settings.${si}.max`)
                            )}
                            helperText={
                              getIn(touched, `settings.${si}.max`) &&
                              getIn(errors, `settings.${si}.max`)
                            }
                            onChange={(e) => {
                              const name = e.target.name;
                              const value = e.target.value;

                              if (Number(value) >= 1) {
                                setFieldValue(name, value);
                              }
                            }}
                          />
                        </Stack>

                        {/* <Stack direction={'row'} spacing={2} flex={1}>
                          <RadioGroup
                            value={type}
                            onChange={handleChangeType}
                            sx={{
                              flexDirection: 'row',
                              justifyContent: 'space-around',
                            }}
                          >
                            <FormControlLabel
                              label="Opciones"
                              value={SettingType.OPTIONS}
                              control={<Radio size="small" color="primary" />}
                            />
                            <FormControlLabel
                              label="Número"
                              value={SettingType.NUMBER}
                              control={<Radio size="small" color="primary" />}
                              disabled
                            />
                          </RadioGroup>
                        </Stack> */}
                      </Stack>

                      {type === SettingType.OPTIONS ? (
                        <Stack spacing={2}>
                          <Divider
                            sx={{
                              fontSize: '0.80rem',
                              textTransform: 'uppercase',
                              color: 'GrayText',
                            }}
                          >
                            Opciones
                          </Divider>

                          {s.options?.map((o: any, oi: number) => (
                            <Stack
                              spacing={2}
                              key={`${o}-${oi}`}
                              direction="row"
                            >
                              {Number(s.max) === 1 ? (
                                <RadioGroup
                                  value={`default-option-${si}.defaultOption`}
                                  onChange={() => {
                                    setFieldValue(
                                      `settings.${si}.defaultOption`,
                                      o.id
                                    );
                                  }}
                                >
                                  <Radio
                                    checked={
                                      (values.settings as Setting[])[si]
                                        .defaultOption === o.id
                                    }
                                    disabled={o?.type === 'subtitle'}
                                    sx={{
                                      opacity: o?.type === 'subtitle' ? 0 : 1,
                                    }}
                                  />
                                </RadioGroup>
                              ) : (
                                <FormGroup>
                                  <Checkbox
                                    checked={
                                      (values.settings as Setting[])[
                                        si
                                      ]?.defaultOption?.includes(o.id) ?? false
                                    }
                                    disabled={
                                      o?.type === 'subtitle'
                                        ? true
                                        : ((values.settings as Setting[])[si]
                                            ?.defaultOption?.length ?? 0) >=
                                            (s?.max ?? 0) &&
                                          !(values.settings as Setting[])[
                                            si
                                          ]?.defaultOption?.includes(o.id)
                                    }
                                    onChange={() => {
                                      const setting = (
                                        values.settings as Setting[]
                                      )[si];
                                      const defaultOptionArray = Array.isArray(setting?.defaultOption)
                                        ? setting.defaultOption
                                        : [];
                                      const newDefaultOption =
                                        defaultOptionArray.includes(o.id)
                                          ? defaultOptionArray.filter(
                                              (id: string) => id !== o.id
                                            )
                                          : [...defaultOptionArray, o.id];
                                      setFieldValue(
                                        `settings.${si}.defaultOption`,
                                        newDefaultOption
                                      );
                                    }}
                                    sx={{
                                      opacity: o?.type === 'subtitle' ? 0 : 1,
                                    }}
                                  />
                                </FormGroup>
                              )}
                              <InputText
                                label={`Opción ${oi + 1}`}
                                name={`settings[${si}].options.${oi}.title`}
                                value={
                                  (values.settings as Setting[])[si].options?.[
                                    oi
                                  ].title ?? ''
                                }
                                size="small"
                                error={Boolean(
                                  getIn(
                                    touched,
                                    `settings.${si}.options.${oi}.title`
                                  ) &&
                                    getIn(
                                      errors,
                                      `settings.${si}.options.${oi}.title`
                                    )
                                )}
                                helperText={
                                  getIn(
                                    touched,
                                    `settings.${si}.options.${oi}.title`
                                  ) &&
                                  getIn(
                                    errors,
                                    `settings.${si}.options.${oi}.title`
                                  )
                                }
                              />
                              <Stack flex={1} direction={'row'} spacing={2}>
                                {o.type !== 'subtitle' ? (
                                  <InputText
                                    label="Precio"
                                    name={`settings[${si}].options.${oi}.price`}
                                    value={
                                      (values.settings as Setting[])[si]
                                        .options?.[oi].price ?? ''
                                    }
                                    size="small"
                                    sx={{ width: 100 }}
                                    error={Boolean(
                                      getIn(
                                        touched,
                                        `settings.${si}.options.${oi}.price`
                                      ) &&
                                        getIn(
                                          errors,
                                          `settings.${si}.options.${oi}.price`
                                        )
                                    )}
                                    helperText={
                                      getIn(
                                        touched,
                                        `settings.${si}.options.${oi}.price`
                                      ) &&
                                      getIn(
                                        errors,
                                        `settings.${si}.options.${oi}.price`
                                      )
                                    }
                                  />
                                ) : null}

                                <Tooltip
                                  title="Eliminar Opción"
                                  arrow
                                  placement="top"
                                >
                                  <IconButton
                                    color="error"
                                    onClick={() => {
                                      const setting = {
                                        ...(values.settings as Setting[])[si],
                                      };
                                      const options = setting?.options?.filter(
                                        (_, index) => index !== oi
                                      );
                                      replace(si, {
                                        ...setting,
                                        options,
                                      });
                                    }}
                                  >
                                    <Trash />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                              <Divider />
                            </Stack>
                          ))}
                          <Stack direction="row" spacing={2}>
                            <Button
                              variant="text"
                              startIcon={<Plus />}
                              onClick={() => addOption(si)}
                            >
                              Opción
                            </Button>
                            <Button
                              variant="text"
                              startIcon={<Plus />}
                              onClick={() => addOption(si, 'subtitle')}
                            >
                              Subtítulo
                            </Button>
                          </Stack>
                        </Stack>
                      ) : null}
                    </Stack>
                  )}
                </Paper>
              ))}

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Button
                  variant="outlined"
                  startIcon={<Plus />}
                  onClick={() => push(EMPTY_SETTING)}
                >
                  Agregar Configuración
                </Button>
              </Box>
            </Stack>
          );
        }}
      </FieldArray>
    </Stack>
  );
};

export default Settings;
